import { api } from '../console/api.ts';
import { empty, field, pageHead } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import { date, esc, icon } from '../console/format.ts';
import {
  NOTICE_TONE,
  type NoticeTone,
  noticeHtml,
  type PlatformNotice,
} from '../console/notice.ts';
import { type Actions, current, render } from '../console/router.ts';
import { administersNubi } from '../console/session.ts';
import { $, act } from '../console/ui.ts';
import { dateControl, REQUIRED_HINT, selectControl, textareaControl } from '../controls.ts';

/**
 * «Administración de Nubi › Aviso a las agencias» (#284, ADR-0028): the one notice every agency
 * sees as a band above its screens — plain text of up to 280 characters, «Información» or
 * «Importante», with an optional last day. The administration publishes it, changes it (a new
 * text or tone shows it again to whoever had closed it; a new last day alone does not) and
 * clears it; each in «Bitácora». Its view id is `aviso-global`: `avisos` is a person's inbox.
 */

export type NoticeAnswer = {
  /** Today in Guatemala, as the API decides whether the notice is in force. */
  today: string;
  notice: (PlatformNotice & { inForce: boolean; publishedAt: string; updatedAt: string }) | null;
};

export const NOTICE_MAX_LENGTH = 280;

/** The answer of the last paint (the form reads it). */
let shown: NoticeAnswer | null = null;

/** Back to a fresh screen (a new session). */
export function resetAvisoGlobal() {
  shown = null;
}

/** What the notice there is means for the agencies today. */
function standing(n: NonNullable<NoticeAnswer['notice']>) {
  if (!n.inForce && n.endsOn) return `Venció el ${date(n.endsOn)}: las agencias ya no lo ven.`;
  return n.endsOn
    ? `Vigente hasta el ${date(n.endsOn)}, incluido: lo ven todas las agencias.`
    : 'Vigente hasta que lo quites: lo ven todas las agencias.';
}

/** The notice there is, as the agencies see it, and «Quitar aviso»; or that there is none. */
function currentHtml(a: NoticeAnswer) {
  const n = a.notice;
  if (!n)
    return `<section class="card" data-component="notice-current" aria-labelledby="notice-current-title">
      <div class="card-head"><h2 id="notice-current-title">Aviso publicado</h2></div>
      <p class="muted notice-none">No hay un aviso publicado: las agencias no ven ninguna banda.</p>
    </section>`;
  return `<section class="card" data-component="notice-current" aria-labelledby="notice-current-title">
    <div class="card-head"><h2 id="notice-current-title">Aviso publicado</h2><span class="spacer"></span>
      <button type="button" class="btn sm danger" data-notice-clear>Quitar aviso</button></div>
    <p class="muted small notice-lead">Así lo ven las personas de las agencias, arriba de cada pantalla:</p>
    <div class="platform-notice preview" data-component="notice-preview">${noticeHtml(n, false)}</div>
    <p class="note ${n.inForce ? 'info' : 'bad'}">${icon(n.inForce ? 'ok' : 'alert')}<span>${esc(standing(n))}</span></p>
  </section>`;
}

/** The form to publish the notice, or to change the one there is. */
function formHtml(a: NoticeAnswer) {
  const n = a.notice;
  const tone: NoticeTone = n?.tone ?? 'INFO';
  return `<section class="card" data-component="notice-form-card" aria-labelledby="notice-form-title">
    <div class="card-head"><h2 id="notice-form-title">${n ? 'Cambiar el aviso' : 'Publicar un aviso'}</h2></div>
    <form id="notice-form" novalidate><div class="form-grid notice-grid">
      ${REQUIRED_HINT}
      <div class="notice-wide">${field(
        'Texto del aviso',
        textareaControl({
          id: 'notice-text',
          rows: 3,
          maxlength: NOTICE_MAX_LENGTH,
          value: n?.text ?? '',
          placeholder: 'Mantenimiento el sábado de 22:00 a 23:00',
          hint: `Texto plano, sin < ni >: hasta ${NOTICE_MAX_LENGTH} caracteres.`,
        }),
        { required: true },
      )}</div>
      ${field(
        'Tono',
        selectControl(
          { id: 'notice-tone', value: tone },
          (Object.keys(NOTICE_TONE) as NoticeTone[]).map((t) => ({
            value: t,
            label: NOTICE_TONE[t].label,
            selected: t === tone,
          })),
        ),
        { required: true },
      )}
      ${field(
        'Vigente hasta',
        dateControl({
          id: 'notice-ends',
          value: n?.endsOn ?? '',
          min: a.today,
          hint: 'Opcional: lo ven hasta ese día, incluido. Sin fecha, hasta que lo quites.',
        }),
      )}
      <p class="muted small notice-wide">${
        n
          ? 'Si cambias el texto o el tono, quien lo cerró lo vuelve a ver. Si solo cambias la fecha, no.'
          : 'Lo ven todas las personas de las agencias, arriba de cada pantalla; cada una lo puede cerrar en su sesión.'
      }</p></div>
      <div class="form-foot"><span class="spacer"></span>
        <button type="submit" class="btn primary" id="notice-submit">${n ? 'Guardar cambios' : 'Publicar aviso'}</button></div>
    </form>
  </section>`;
}

export async function avisoGlobal() {
  const description =
    'El aviso que todas las agencias ven arriba de sus pantallas: uno a la vez, en texto plano, con tono de información o importante y una fecha fin opcional.';
  const head = pageHead('Aviso a las agencias', description);
  if (!administersNubi())
    return `${head}${empty('lock', 'Esta sección es de la administración de Nubi', 'Tu rol no publica avisos a las agencias.', '', 2)}`;
  const answer = await api<NoticeAnswer>('GET', '/v1/platform/notice');
  shown = answer;
  return `${head}${currentHtml(answer)}${formHtml(answer)}`;
}

/** Paints the screen again and puts the focus where the person continues. */
async function repaint(focus: string) {
  await render(current, { quiet: true });
  document.querySelector<HTMLElement>(focus)?.focus();
}
const value = (id: string) =>
  String(($(`#${id}`) as (HTMLElement & { value?: unknown }) | null)?.value ?? '');

export const actions: Actions = {
  click: {
    noticeClear: (b) => {
      void act(b, 'Quitando el aviso…', async () => {
        await api('DELETE', '/v1/platform/notice');
        await repaint('#notice-current-title');
        return 'Aviso quitado: las agencias ya no lo ven.';
      });
    },
  },
  submit: {
    'notice-form': (ev) => {
      ev.preventDefault();
      const form = ev.target as HTMLFormElement;
      const before = shown?.notice ?? null;
      void act(
        form.querySelector<HTMLElement>('[type="submit"]'),
        'Guardando el aviso…',
        async () => {
          // The same rules as the API: one line of plain text.
          const text = value('notice-text').replace(/\s+/g, ' ').trim();
          if (!text) throw new InputError('Escribe el texto del aviso.', 'notice-text');
          if (/[<>]/.test(text))
            throw new InputError('El aviso es texto plano: quita los signos < y >.', 'notice-text');
          if (text.length > NOTICE_MAX_LENGTH)
            throw new InputError(
              `El aviso tiene hasta ${NOTICE_MAX_LENGTH} caracteres; este tiene ${text.length}.`,
              'notice-text',
            );
          const tone = value('notice-tone') === 'IMPORTANT' ? 'IMPORTANT' : 'INFO';
          const endsOn = value('notice-ends') || null;
          await api('PUT', '/v1/platform/notice', { text, tone, endsOn });
          await repaint('#notice-current-title');
          return before
            ? 'Aviso guardado: las agencias ven el cambio al entrar.'
            : 'Aviso publicado para todas las agencias.';
        },
      );
    },
  },
};
